import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Globe, Building2, Users, MessageSquare, Clock, Calendar, Settings } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";

interface ManagedReviewStepProps {
  data: ManagedCampaignData;
  clientName?: string;
  hideSchedule?: boolean;
  hideDailyLimits?: boolean;
  scheduleCardTitle?: string;
}

const DAYS_MAP: Record<number, string> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

export const ManagedReviewStep = ({ data, clientName, hideSchedule = false, hideDailyLimits = false, scheduleCardTitle }: ManagedReviewStepProps) => {
  const activeSequences = data.sequences.filter(s => s.action_type !== "end");
  const is24x7 = data.workingHoursStart === 0 && data.workingHoursEnd === 24 && data.workingDays.length === 7;

  return (
    <div className="space-y-6">
      {/* Campaign Info */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Campaign Name</Label>
          <span className="font-semibold">{data.campaignName}</span>
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <Label className="text-sm font-medium">LinkedIn Account</Label>
          <span className="text-sm">{clientName || "To be assigned by admin"}</span>
        </div>
      </Card>

      {/* Schedule Summary */}
      {!hideSchedule && (
        <Card className="p-4 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Settings className="w-4 h-4" /> {scheduleCardTitle || (hideDailyLimits ? "Schedule" : "Schedule & Limits")}
          </h3>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-xs text-muted-foreground">Timezone</Label>
              <p className="font-medium">{data.timezone}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Working Hours</Label>
              <p className="font-medium">
                {is24x7 ? (
                  <Badge variant="secondary">24/7</Badge>
                ) : (
                  `${data.workingHoursStart.toString().padStart(2, "0")}:00 - ${data.workingHoursEnd.toString().padStart(2, "0")}:00`
                )}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Working Days</Label>
              <p className="font-medium">
                {is24x7 ? (
                  "Every day"
                ) : (
                  data.workingDays.map(d => DAYS_MAP[d]).join(", ")
                )}
              </p>
            </div>
            {!hideDailyLimits && (
              <div>
                <Label className="text-xs text-muted-foreground">Daily Limits</Label>
                <p className="font-medium">
                  {data.maxConnectionsDaily} connections, {data.maxMessagesDaily} messages
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Target Audience Summary */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Globe className="w-4 h-4" /> Target Audience
        </h3>

        {/* Show Sales Navigator URL for sales_nav mode */}
        {data.targetAcquisitionMode === 'sales_nav' && data.salesNavUrl ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Sales Navigator Search</Label>
              <a 
                href={data.salesNavUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View Search Link →
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              Our team will use this link to find and add targets to your campaign.
            </p>
          </div>
        ) : data.targetAcquisitionMode === 'csv' && data.uploadedTargets && data.uploadedTargets.length > 0 ? (
          /* Show uploaded targets for CSV mode */
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Uploaded from CSV</Label>
              <Badge variant="secondary" className="text-xs">
                {data.uploadedTargets.length} targets
              </Badge>
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-md relative">
              <table className="w-full text-sm">
                <thead className="bg-background sticky top-0 z-10 shadow-sm">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium bg-background">Name</th>
                    <th className="text-left p-2 font-medium bg-background">Company</th>
                    <th className="text-left p-2 font-medium bg-background">Title</th>
                  </tr>
                </thead>
                <tbody>
                  {data.uploadedTargets.slice(0, 50).map((target, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-2">
                        {target.first_name || target.last_name 
                          ? `${target.first_name || ''} ${target.last_name || ''}`.trim()
                          : target.name || '-'}
                      </td>
                      <td className="p-2 text-muted-foreground">{target.company || '-'}</td>
                      <td className="p-2 text-muted-foreground">{target.title || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.uploadedTargets.length > 50 && (
                <div className="text-center text-xs text-muted-foreground py-2 border-t bg-muted/30">
                  + {data.uploadedTargets.length - 50} more targets
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Geography */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Countries</Label>
              <div className="flex flex-wrap gap-1">
                {data.countries.map(c => (
                  <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                ))}
              </div>
              {data.cities.length > 0 && (
                <>
                  <Label className="text-xs text-muted-foreground">Cities</Label>
                  <div className="flex flex-wrap gap-1">
                    {data.cities.map(c => (
                      <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Industry */}
            <div className="space-y-2 border-t pt-3">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Industries
              </Label>
              <div className="flex flex-wrap gap-1">
                {data.industries.map(i => (
                  <Badge key={i} variant="secondary" className="text-xs">{i}</Badge>
                ))}
              </div>
              {data.companySizes.length > 0 && (
                <>
                  <Label className="text-xs text-muted-foreground">Company Sizes</Label>
                  <div className="flex flex-wrap gap-1">
                    {data.companySizes.map(s => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* People */}
            <div className="space-y-2 border-t pt-3">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="w-3 h-3" /> Departments
              </Label>
              <div className="flex flex-wrap gap-1">
                {data.departments.map(d => (
                  <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                ))}
              </div>
              {data.seniorityLevels.length > 0 && (
                <>
                  <Label className="text-xs text-muted-foreground">Seniority</Label>
                  <div className="flex flex-wrap gap-1">
                    {data.seniorityLevels.map(s => (
                      <Badge key={s} variant="outline" className="text-xs capitalize">{s}</Badge>
                    ))}
                  </div>
                </>
              )}
              {data.jobTitles.length > 0 && (
                <>
                  <Label className="text-xs text-muted-foreground">Job Titles</Label>
                  <div className="flex flex-wrap gap-1">
                    {data.jobTitles.map(t => (
                      <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Keywords (include + exclude) */}
            {(data.companyKeywords.length > 0 || (data.personKeywords?.length || 0) > 0 ||
              (data.excludeCompanyKeywords?.length || 0) > 0 || (data.excludePersonKeywords?.length || 0) > 0) && (
              <div className="space-y-3 border-t pt-3">
                {(data.companyKeywords.length > 0 || (data.excludeCompanyKeywords?.length || 0) > 0) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Company Keywords</Label>
                    <div className="flex flex-wrap gap-1">
                      {data.companyKeywords.map(k => (
                        <Badge key={`c-inc-${k}`} variant="secondary" className="text-xs">{k}</Badge>
                      ))}
                      {(data.excludeCompanyKeywords || []).map(k => (
                        <Badge key={`c-exc-${k}`} variant="destructive" className="text-xs">− {k}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {((data.personKeywords?.length || 0) > 0 || (data.excludePersonKeywords?.length || 0) > 0) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Person Keywords</Label>
                    <div className="flex flex-wrap gap-1">
                      {(data.personKeywords || []).map(k => (
                        <Badge key={`p-inc-${k}`} variant="secondary" className="text-xs">{k}</Badge>
                      ))}
                      {(data.excludePersonKeywords || []).map(k => (
                        <Badge key={`p-exc-${k}`} variant="destructive" className="text-xs">− {k}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Sequence Summary */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Message Sequence
        </h3>
        <div className="space-y-2">
          {activeSequences.map((step, index) => (
            <div key={index} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium flex-shrink-0">
                {step.step_number}
              </div>
              <div className="flex-1">
                <div className="font-medium">
                  {step.action_type === "connect" ? "Connection Request" : "Follow-up Message"}
                </div>
                {step.delay_hours > 0 && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Wait {step.delay_hours} hours
                  </div>
                )}
                {step.message_template && (
                  <div className="text-sm text-foreground/90 mt-2 bg-muted/30 p-3 rounded-md border text-wrap whitespace-pre-wrap leading-relaxed">
                    {step.message_template}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Notes */}
      {data.additionalNotes && (
        <Card className="p-4 space-y-2">
          <Label className="text-sm font-medium">Additional Notes</Label>
          <p className="text-sm text-muted-foreground">{data.additionalNotes}</p>
        </Card>
      )}

      {/* Setup Notice */}

    </div>
  );
};
